import type { Theme, ThemeOptions } from "@mui/material";
import { createTheme, darken, getContrastRatio, lighten } from "@mui/material";

import type { BrandingConfig } from "@utilifeed/config/branding";
import { propaiTheme } from "@utilifeed/config/propai.theme";

const COLOR_TINT = 0.3;

// Standard font weights
const FONT_WEIGHTS = {
  light: 300,
  regular: 400,
  medium: 500,
  bold: 700,
} as const;

const TYPOGRAPHY_VARIANTS = [
  // Standard MUI variants
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "subtitle1",
  "subtitle2",
  "body1",
  "body2",
  "button",
  "caption",
  "overline",
  // Semantic variants
  "displayLarge",
  "displaySmall",
  "titleLarge",
  "titleMedium",
  "titleSmall",
  "labelLarge",
  "labelMedium",
  "bodyLarge",
  "bodyMedium",
  "bodySmall",
] as const;

const toPaletteColor = (main: string) => ({
  main,
  light: lighten(main, COLOR_TINT),
  dark: darken(main, COLOR_TINT),
  contrastText:
    getContrastRatio(main, "#000000") > getContrastRatio(main, "#ffffff") ? "#000000" : "#ffffff",
});

const applyFontFamily = (fontFamily: string) =>
  TYPOGRAPHY_VARIANTS.reduce(
    (acc, variant) => {
      acc[variant] = { ...propaiTheme.typography[variant], fontFamily };
      return acc;
    },
    {} as Record<string, { fontFamily: string }>
  );

/**
 * Pure mapper: BrandingConfig → MUI Theme.
 * Expects a complete BrandingConfig — all fallback logic lives in BrandingProvider.
 */
export function createThemeFromBranding(
  branding: BrandingConfig,
  resolvedFontFamily?: string
): Theme {
  const { colors } = branding;

  const primary = toPaletteColor(colors.primary);
  const secondary = toPaletteColor(colors.secondary);
  const tertiary = toPaletteColor(colors.tertiary);

  const fontFamily = resolvedFontFamily ?? branding.typography.fontFamily;

  const options: ThemeOptions = {
    palette: {
      mode: "light",
      primary: { ...primary, light: colors.primaryLight, dark: colors.primaryDark },
      secondary,
      ...(tertiary && { tertiary }),
      background: {
        default: colors.container,
        paper: colors.surface,
      },
      text: {
        primary: colors.fontPrimary,
        secondary: colors.fontSecondary,
      },
      divider: colors.divider1,
      inverseSurface: colors.inverseSurface,
      outline: toPaletteColor(colors.outline),
    } as ThemeOptions["palette"],
    typography: {
      ...propaiTheme.typography,
      fontFamily,
      fontWeightLight: FONT_WEIGHTS.light,
      fontWeightRegular: FONT_WEIGHTS.regular,
      fontWeightMedium: FONT_WEIGHTS.medium,
      fontWeightBold: FONT_WEIGHTS.bold,
      ...applyFontFamily(fontFamily),
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: { backgroundColor: colors.primaryDark },
          colorPrimary: { backgroundColor: colors.primaryDark },
        },
      },
    },
  };

  return createTheme(propaiTheme, options);
}
